import { useEffect, useState } from "react";

type HeroBgOverLayerProps = {
  backgrounds: string[];
  duration?: number; // in seconds for each background
};

export function HeroBgOverLayer({
  backgrounds,
  duration = 4,
}: HeroBgOverLayerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (backgrounds.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % backgrounds.length);
    }, duration * 1000);

    return () => clearInterval(interval);
  }, [backgrounds.length, duration]);

  return (
    <div className="absolute inset-0 size-full">
      {/* Background Images with Simple Fade */}
      <div className="absolute inset-0 size-full">
        {backgrounds.map((bg, index) => (
          <img
            key={index}
            src={bg}
            alt={`Hero background ${index + 1}`}
            className={`absolute inset-0 size-full object-cover transition-opacity duration-500 ease-in-out ${
              index === currentIndex ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
      </div>

      {/* SVG Overlay with Effects and Filters */}
      <svg
        viewBox="0 0 1920 1080"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        preserveAspectRatio="xMidYMid slice"
      >
        <g filter="url(#filter0_f_50_3232)">
          <ellipse
            cx="947.002"
            cy="76"
            rx="188"
            ry="1362"
            transform="rotate(-90 947.002 76)"
            fill="#000C2C"
          />
        </g>
        <g filter="url(#filter1_f_50_3232)">
          <ellipse
            cx="946.98"
            cy="1051"
            rx="213"
            ry="2284"
            transform="rotate(88.2278 946.98 1051)"
            fill="#001959"
          />
        </g>
        <ellipse
          cx="776.002"
          cy="524"
          rx="1910"
          ry="772"
          fill="url(#paint0_radial_50_3232)"
        />
        <g opacity="0.33" filter="url(#filter2_f_50_3232)">
          <ellipse
            cx="465.502"
            cy="391"
            rx="576"
            ry="1158.5"
            transform="rotate(-90 465.502 391)"
            fill="#000C2C"
          />
        </g>
        <g opacity="0.33" filter="url(#filter3_f_50_3232)">
          <ellipse
            cx="1662.48"
            cy="911"
            rx="353"
            ry="709.5"
            transform="rotate(-90 1662.48 911)"
            fill="#000C2C"
          />
        </g>
        <g filter="url(#filter4_f_50_3232)">
          <ellipse
            cx="1770.48"
            cy="1051"
            rx="213"
            ry="2284"
            transform="rotate(88.2278 1770.48 1051)"
            fill="#001959"
          />
        </g>

        <defs>
          <filter
            id="filter0_f_50_3232"
            x="-599.841"
            y="-296.843"
            width="3093.69"
            height="745.686"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="92.4214"
              result="effect1_foregroundBlur_50_3232"
            />
          </filter>
          <filter
            id="filter1_f_50_3232"
            x="-1520.78"
            y="641.787"
            width="4935.52"
            height="818.427"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="92.4214"
              result="effect1_foregroundBlur_50_3232"
            />
          </filter>
          <filter
            id="filter2_f_50_3232"
            x="-877.841"
            y="-369.843"
            width="2686.69"
            height="1521.69"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="92.4214"
              result="effect1_foregroundBlur_50_3232"
            />
          </filter>
          <filter
            id="filter3_f_50_3232"
            x="768.134"
            y="373.157"
            width="1788.69"
            height="1075.69"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="92.4214"
              result="effect1_foregroundBlur_50_3232"
            />
          </filter>
          <filter
            id="filter4_f_50_3232"
            x="-697.276"
            y="641.787"
            width="4935.52"
            height="818.427"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="92.4214"
              result="effect1_foregroundBlur_50_3232"
            />
          </filter>
          <radialGradient
            id="paint0_radial_50_3232"
            cx="0"
            cy="0"
            r="1"
            gradientTransform="matrix(956.401 833.816 -1233.98 1902.28 936.23 496.506)"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#000C2C" stopOpacity="0" />
            <stop offset="1" stopColor="#000C2C" />
          </radialGradient>
        </defs>
      </svg>
    </div>
  );
}
